import { useEffect, useMemo, type ReactNode } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  defaultFeatureFlags,
  FeatureFlagsContext,
  type FeatureFlagsContextValue,
} from "./feature-flags-context";
import {
  LD_INTEGRATOR_SYNC_FLAG,
  LD_LIVE_CREDENTIALS_FLAG,
} from "./flag-keys";
import { publishFeatureFlags } from "./flag-storage";
import { useLaunchDarklyFlagsResolved } from "./useLaunchDarklyFlagsResolved";

/** Keeps the Markdoc docs topnav (outside React) on the evaluated flags. */
function usePublishFeatureFlags(value: FeatureFlagsContextValue) {
  useEffect(() => {
    if (!value.flagsResolved) {
      // Keep the last mirrored values so the docs topnav does not flash legacy.
      return;
    }

    publishFeatureFlags({
      sandboxCredentialsEnabled: true,
    });
  }, [value]);
}

function LaunchDarklyFeatureFlagsBridge({ children }: { children: ReactNode }) {
  const flags = useFlags();
  const flagsResolved = useLaunchDarklyFlagsResolved();
  const value = useMemo(
    () => ({
      // Fail-closed: only true when LD explicitly returns true.
      integratorSyncEnabled: flags[LD_INTEGRATOR_SYNC_FLAG] === true,
      liveCredentialsEnabled: flags[LD_LIVE_CREDENTIALS_FLAG] === true,
      flagsResolved,
    }),
    [flags, flagsResolved],
  );
  usePublishFeatureFlags(value);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

function DefaultFeatureFlags({ children }: { children: ReactNode }) {
  usePublishFeatureFlags(defaultFeatureFlags);

  return (
    <FeatureFlagsContext.Provider value={defaultFeatureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

interface FeatureFlagsProviderProps {
  children: ReactNode;
  launchDarklyEnabled: boolean;
}

export function FeatureFlagsProvider({
  children,
  launchDarklyEnabled,
}: FeatureFlagsProviderProps) {
  if (launchDarklyEnabled) {
    return (
      <LaunchDarklyFeatureFlagsBridge>
        {children}
      </LaunchDarklyFeatureFlagsBridge>
    );
  }

  return <DefaultFeatureFlags>{children}</DefaultFeatureFlags>;
}
